import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import config from "../../../aws-exports";
import {
  Visibility,
  Share,
  AttachFile,
  Refresh,
  Delete,
} from "@mui/icons-material";
import { useLoading } from "../../../context/LoadingContext";
import {
  fetchData,
  postQuery,
  uploadVideoToS3,
} from "../../Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../Requirement/components/common/AlerModel";
import dayjs from "dayjs";
import { getParameterFromManager } from "../../../services/Utils";

const Invoices = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeUser, setActiveUser] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [errors, setErrors] = useState({
    file: "",
    documentName: "",
    category: "",
    tags: "",
    expiryDate: "",
  });
  const [searchInputType, setSearchInputType] = useState("text");
  const [INRToUsd, setINRToUSD] = useState(0.012);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilter = (event) => {
    const value = event.target.value;
    setFilter(value);
    setSearchTerm(""); // Reset search term when filter changes
    // Set input type based on filter
    if (value === "searchDateUploaded" || value === "searchExpiryDate") {
      setSearchInputType("date");
    } else {
      setSearchInputType("text");
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setDocumentName("");
    setCategory("");
    setTags([]);
    setCurrentTag("");
    setExpiryDate("");
    setErrors({
      file: "",
      documentName: "",
      category: "",
      tags: "",
      expiryDate: "",
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    if (!file) {
      setErrors((prev) => ({ ...prev, file: "Please select a file." }));
    } else if (
      !["application/pdf", "image/jpeg", "image/png"].includes(file.type)
    ) {
      setErrors((prev) => ({
        ...prev,
        file: "Only PDF, JPEG, or PNG files are allowed.",
      }));
    } else if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: "File size must be less than 5MB.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  const handleDocumentNameChange = (event) => {
    const value = event.target.value;
    setDocumentName(value);
    setErrors((prev) => ({ ...prev, documentName: "" }));
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setCategory(value);
    if (!value) {
      setErrors((prev) => ({ ...prev, category: "Please select a category." }));
    } else {
      setErrors((prev) => ({ ...prev, category: "" }));
    }
  };

  const handleTagInputChange = (event) => {
    setCurrentTag(event.target.value);
  };

  const handleTagKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      const trimmedTag = currentTag.trim();
      if (trimmedTag && trimmedTag.length >= 2 && !tags.includes(trimmedTag)) {
        setTags([...tags, trimmedTag]);
        setCurrentTag("");
        setErrors((prev) => ({ ...prev, tags: "" }));
      } else if (trimmedTag && trimmedTag.length < 2) {
        setErrors((prev) => ({
          ...prev,
          tags: "Each tag must be at least 2 characters long.",
        }));
      }
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleExpiryDateChange = (event) => {
    const value = event.target.value;
    setExpiryDate(value);
    if (value) {
      const selectedDate = new Date(value);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Reset time for comparison
      if (selectedDate < currentDate) {
        setErrors((prev) => ({
          ...prev,
          expiryDate: "Expiry date cannot be earlier than today.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, expiryDate: "" }));
      }
    } else {
      setErrors((prev) => ({ ...prev, expiryDate: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      file: "",
      documentName: "",
      category: "",
      tags: "",
      expiryDate: "",
    };
    let isValid = true;

    if (!selectedFile) {
      newErrors.file = "Please select a file.";
      isValid = false;
    } else if (
      !["application/pdf", "image/jpeg", "image/png"].includes(
        selectedFile.type
      )
    ) {
      newErrors.file = "Only PDF, JPEG, or PNG files are allowed.";
      isValid = false;
    } else if (selectedFile.size > 5 * 1024 * 1024) {
      newErrors.file = "File size must be less than 5MB.";
      isValid = false;
    }

    if (!category) {
      newErrors.category = "Please select a category.";
      isValid = false;
    }

    if (tags.some((tag) => tag.length < 2)) {
      newErrors.tags = "Each tag must be at least 2 characters long.";
      isValid = false;
    }

    if (expiryDate) {
      const selectedDate = new Date(expiryDate);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Reset time for comparison
      if (selectedDate < currentDate) {
        newErrors.expiryDate = "Expiry date cannot be earlier than today.";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleUpload = async () => {
    if (validateForm()) {
      try {
        setLoading(true);
        let fileRef = "";
        let fileRefUrl = "";
        if (selectedFile instanceof File) {
          console.log("Uploading file:", selectedFile);
          const result = await uploadVideoToS3(selectedFile);
          fileRef = result.fileName;
          fileRefUrl = result.url;
          console.log("File uploaded to S3:", { fileRef, fileRefUrl });

          let userDocumentInput = {
            DocumentId: uuidv4(),
            DocumentName: documentName.trim() || null,
            DocumentFile: fileRef,
            Category: category,
            CreatedBy: activeUser,
            ExpireAt: expiryDate || null,
            Tag: tags.length > 0 ? tags.join(",") : null,
          };

          if (!expiryDate) {
            userDocumentInput = {
              DocumentId: uuidv4(),
              DocumentName: documentName.trim() || null,
              DocumentFile: fileRef,
              Category: category,
              CreatedBy: activeUser,
              Tag: tags.length > 0 ? tags.join(",") : null,
            };
          }
          const body = {
            target: "createUserDocument",
            userDocumentInput,
          };

          const result1 = await postQuery(body);
          if (!result1 || !result1.items) {
            throw new Error(`Failed to create user document.`);
          }
          console.log("Document saved successfully:", result1);
        }
        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: `Document uploaded successfully.`,
          autoClose: false,
          nextAction: () => {
            handleCloseModal();
            fetchDocuments();
          },
        });
      } catch (error) {
        console.error("Error uploading file to S3 or saving document:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error!",
          message:
            error.message ||
            `Failed to upload file or save document. Please try again.`,
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleVisibilityClick = (doc) => {
    if (doc.documentpath && doc.document) {
      const fullPath = `${doc.documentpath}${doc.document}`;
      console.log(`Opening document at: ${fullPath}`);
      window.open(fullPath, "_blank");
    } else {
      console.error(`Document path or file is missing for document ${doc.id}`);
      setErrors((prev) => ({
        ...prev,
        file: "Unable to open document. File path is missing.",
      }));
    }
  };

  const handleShareClick = (docId) => {
    console.log(`Share clicked for document ${docId}`);
  };

  const handleAttachClick = (docId) => {
    console.log(`Attach clicked for document ${docId}`);
  };

  const handleRefreshClick = (docId) => {
    console.log(`Refresh clicked for document ${docId}`);
  };

  const handleDeleteClick = (docId) => {
    console.log(`Delete clicked for document ${docId}`);
  };

  const handleTagClick = (tag, docId) => {
    console.log(`Tag "${tag}" clicked for document ${docId}`);
    setFilter(tag);
  };

  const loadDocumentCategory = useCallback(async () => {
    setLoading(true);
    try {
      const [allDocumentCategory] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "DocCategory" },
        }),
      ]);
      if (allDocumentCategory.items.length > 0) {
        setCategories(allDocumentCategory.items);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error loading Categories: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const bucket = process.env.REACT_APP_AMTPL_BUCKET;
      const filePath = `public/media/amtpl/`;
      const fileUrl = `https://${bucket}.s3.${config.aws_project_region}.amazonaws.com/${filePath}`;

      // Build filterTerms based on filter and searchTerm
      const filterTerms = { Category: "Invoices" };
      if (searchTerm) {
        if (filter === "searchDateUploaded" && searchInputType === "date") {
          // Format date to match backend (assuming ISO format or DD MMM YYYY)
          filterTerms.CreatedAtLT =
            "'" + dayjs(searchTerm).format("YYYY-MM-DD").toString() + "'";
        } else if (
          filter === "searchExpiryDate" &&
          searchInputType === "date"
        ) {
          filterTerms.ExpireAtLT =
            "'" + dayjs(searchTerm).format("YYYY-MM-DD").toString() + "'";
        } else {
          // Default to document name search
          filterTerms.DocumentName = searchTerm;
        }
      }

      let response = await fetchData({
        target: "listUserDocument",
        userOrder: "DocumentId",
        rowStart: 0,
        pageSize: 1000,
        // filterTerms,
        DocumentName: searchTerm,
        Category: "Invoices",
      });

      if (searchInputType === "date") {
        response = await fetchData({
          target: "listUserDocument",
          userOrder: "DocumentId",
          rowStart: 0,
          pageSize: 1000,
          filterTerms,
        });
      }
      const formattedDocuments = response.items.map((doc) => ({
        id: doc.DocumentId,
        type: doc.DocumentName || doc.Category,
        documentpath: fileUrl,
        createdBy: doc.CreatedBy || "",
        document: doc.DocumentFile || "",
        added: doc.CreatedAt
          ? dayjs(doc.CreatedAt).format("DD MMM YYYY")
          : "N/A",
        expires: doc.ExpireAt
          ? dayjs(doc.ExpireAt).format("DD MMM YYYY")
          : "N/A",
        tags: doc.Tag ? doc.Tag.split(",").map((tag) => tag.trim()) : [],
      }));
      setDocuments(formattedDocuments || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Get current date in YYYY-MM-DD format for min attribute
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        } else {
          setActiveUser(user.attributes.email);
        }
        let inrToUsdVal = await getParameterFromManager("INR_TO_USD");
        inrToUsdVal = parseFloat(inrToUsdVal);
        setINRToUSD(inrToUsdVal);
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };
    checkUser();
    loadDocumentCategory();
  }, [navigate, loadDocumentCategory]);

  useEffect(() => {
    if (activeUser) {
      console.log("filter!!!!", filter);
      fetchDocuments();
    }
  }, [activeUser, searchTerm, filter]); // Added filter to dependencies

  return (
    <div className="kyc-document-container">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="header-doc">
        {searchInputType === "text" ? (
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-bar"
          />
        ) : (
          <input
            type="date"
            value={searchTerm}
            onChange={handleSearch}
            className="search-bar"
            placeholder="Select date"
          />
        )}
        <select
          value={filter}
          onChange={handleFilter}
          className="filter-dropdown"
        >
          <option value="">Filter by...</option>
          <option value="searchDocumentName">Document Name/Tag/Creator</option>
          <option value="searchDateUploaded">Date Uploaded</option>
          <option value="searchExpiryDate">Expiry Date</option>
        </select>
        {/* <button
          className="submit-btn-more"
          onClick={handleOpenModal}
          data-bs-toggle="modal"
          data-bs-target="#uploadModal"
        >
          + Upload Document
        </button> */}
      </div>
      <div className="documents-grid">
        {documents.map((doc) => (
          <div key={doc.id} className="document-card">
            <div
              className="document-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span className="document-icon">
                📄 <span className="document-type">{doc.type}</span>
              </span>
              <Visibility
                style={{ cursor: "pointer" }}
                onClick={() => handleVisibilityClick(doc)}
                title="View Document"
              />
            </div>
            <div className="document-details">
              <p>
                <b>Type:</b> {doc.type}
              </p>
              <p>
                <b>Added:</b> {doc.added}
              </p>
              <p>
                <b>Expires:</b> {doc.expires}
              </p>
              <p>
                <b>Created By:</b>{" "}
                <span className="tag-user">{doc.createdBy}</span>
              </p>
            </div>
            <div className="document-tags">
              {doc.tags.map((tag, index) => (
                <span
                  key={index}
                  className="tag"
                  onClick={() => handleTagClick(tag, doc.id)}
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleTagClick(tag, doc.id);
                    }
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bootstrap Modal (Centered) */}
      <div
        className={`modal fade ${isModalOpen ? "show" : ""}`}
        id="uploadModal"
        tabIndex="-1"
        aria-labelledby="uploadModalLabel"
        aria-hidden={!isModalOpen}
        style={{ display: isModalOpen ? "block" : "none" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="uploadModalLabel">
                Upload New Document
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div
              className="modal-body"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <div className="mb-3">
                <label htmlFor="documentNameInput" className="form-label">
                  Document Name (Optional):
                </label>
                <input
                  type="text"
                  id="documentNameInput"
                  value={documentName}
                  onChange={handleDocumentNameChange}
                  className="form-control"
                  placeholder="Enter document name"
                />
                {errors.documentName && (
                  <div className="text-danger mt-1">{errors.documentName}</div>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="fileInput" className="form-label">
                  Select File: <span className="text-danger">*</span>
                </label>
                <input
                  type="file"
                  id="fileInput"
                  onChange={handleFileChange}
                  className="form-control file-input"
                />
                <small className="form-text text-muted">
                  {selectedFile ? selectedFile.name : "No file chosen"}
                </small>
                {errors.file && (
                  <div className="text-danger mt-1">{errors.file}</div>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="categorySelect" className="form-label">
                  Category: <span className="text-danger">*</span>
                </label>
                <select
                  id="categorySelect"
                  value={category}
                  onChange={handleCategoryChange}
                  className="form-select category-dropdown"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat, index) => (
                    <option key={index} value={cat.Value}>
                      {cat.Value}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <div className="text-danger mt-1">{errors.category}</div>
                )}
              </div>
              <div className="mb-3">
                <label htmlFor="expiryDateInput" className="form-label">
                  Expiry Date (Optional):
                </label>
                <input
                  type="date"
                  id="expiryDateInput"
                  value={expiryDate}
                  onChange={handleExpiryDateChange}
                  className="form-control"
                  min={today}
                />
                {errors.expiryDate && (
                  <div className="text-danger mt-1">{errors.expiryDate}</div>
                )}
              </div>
              <div className="mb-3" style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="tagsInput" className="form-label">
                  Tags (Optional):
                </label>
                <div
                  className="tag-input-container"
                  style={{
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    padding: "8px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    minHeight: "38px",
                    alignItems: "center",
                  }}
                >
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: "#e0e0e0",
                        borderRadius: "16px",
                        padding: "4px 12px",
                        display: "inline-flex",
                        alignItems: "center",
                        fontSize: "14px",
                      }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleTagRemove(tag)}
                        style={{
                          marginLeft: "8px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#666",
                          fontSize: "16px",
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    id="tagsInput"
                    value={currentTag}
                    onChange={handleTagInputChange}
                    onKeyDown={handleTagKeyDown}
                    placeholder={
                      tags.length === 0
                        ? "Enter tags, press Enter or comma to add"
                        : ""
                    }
                    style={{
                      border: "none",
                      outline: "none",
                      flexGrow: 1,
                      fontSize: "14px",
                    }}
                  />
                </div>
                {errors.tags && (
                  <div className="text-danger mt-1">{errors.tags}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="submit-btn-more"
                onClick={handleUpload}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default Invoices;
